import { 
  Task, 
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskStatus,
  PaginationParams,
  User
} from '../types';

// Task list response structure to match backend DTO
interface TaskListResponse {
  data: Task[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create new task
export const createTask = async (data: CreateTaskRequest, files?: File[]): Promise<Task> => {
  const formData = new FormData();
  formData.append('title', data.title);
  formData.append('description', data.description);
  formData.append('assignedToId', data.assignedToId);
  formData.append('dueDate', data.dueDate);
  if (data.priority) {
    formData.append('priority', data.priority);
  }
  
  if (files) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await fetch(`${API_BASE_URL}/tasks`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create task');
  }

  return response.json();
};

// Get tasks list with filtering
export const getTasks = async (params: PaginationParams & { status?: TaskStatus | 'all' } = {}): Promise<TaskListResponse> => {
  const searchParams = new URLSearchParams();
  
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.search) searchParams.append('search', params.search);
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
  if (params.status && params.status !== 'all') searchParams.append('status', params.status);

  const response = await fetch(`${API_BASE_URL}/tasks?${searchParams}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch tasks');
  }

  return response.json();
};

// Get task by ID
export const getTaskById = async (id: string): Promise<Task> => {
  const response = await fetch(`${API_BASE_URL}/tasks/${id}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch task');
  }

  return response.json();
};

// Add update/feedback to task
export const updateTask = async (id: string, data: UpdateTaskRequest): Promise<Task> => {
  const response = await fetch(`${API_BASE_URL}/tasks/${id}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update task');
  }

  return response.json();
};

// Get staff members (for assignment dropdown)
export const getStaffMembers = async (permissionGroupId?: string): Promise<User[]> => {
  const params = new URLSearchParams();
  if (permissionGroupId) params.append('permissionGroupId', permissionGroupId);
  const response = await fetch(`${API_BASE_URL}/users/staff${params.toString() ? `?${params.toString()}` : ''}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch staff members');
  }

  return response.json();
};

// Delete task
export const deleteTask = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/tasks/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete task');
  }
};

// Add update/feedback to task (progress update)
export const addTaskUpdate = async (taskId: string, data: { content: string; markCompleted?: boolean; markInProgress?: boolean }, files?: File[]): Promise<void> => {
  const token = localStorage.getItem('token');
  
  const formData = new FormData();
  formData.append('content', data.content);
  if (data.markCompleted !== undefined) {
    formData.append('markCompleted', data.markCompleted.toString());
  }
  if (data.markInProgress !== undefined) {
    formData.append('markInProgress', data.markInProgress.toString());
  }
  
  if (files) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/updates`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to add task update');
  }
}; 